import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LayoutService } from '../services/layout.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  // When true, `route` is an absolute URL opened in a new tab instead
  // of an Angular router navigation (e.g. external tools like Grafana).
  external?: boolean;
  // SCRUM-2342 — restrict an individual item to specific roles. When set,
  // only those roles see it in the menu (route guards enforce access too).
  roles?: string[];
}

interface NavSection {
  label: string;
  items: NavItem[];
  roles?: string[];
}

@Component({
    selector: 'app-sidebar',
    imports: [CommonModule, RouterModule],
    template: `
    <!-- SCRUM-537: swipe-left to close. Bound at the aside so the gesture
         can start anywhere inside the menu, including over scrollable
         children — we still let vertical drags fall through to scroll. -->
    <aside class="layout-sidebar"
           [class.layout-sidebar-active]="layout.sidebarOpen$ | async"
           [class.tablet-expanded]="layout.tabletRailExpanded$ | async"
           (touchstart)="onTouchStart($event)"
           (touchmove)="onTouchMove($event)"
           (touchend)="onTouchEnd($event)">

      <div class="sidebar-scroll">
        <ul class="layout-menu">
          <ng-container *ngFor="let section of visibleSections">
            <li class="menu-separator">
              <span class="separator-label">{{ section.label }}</span>
            </li>
            <li *ngFor="let item of section.items">
              <a *ngIf="!item.external"
                 [routerLink]="item.route"
                 routerLinkActive="active-route"
                 class="menu-link"
                 [attr.data-testid]="'nav-' + item.label.toLowerCase().replace(/ /g, '-')"
                 (pointerdown)="onLinkPointerDown($event)"
                 (pointerup)="onLinkPointerUp(item.route, $event)"
                 (click)="onMenuItemClick()">
                <i [class]="item.icon + ' menu-icon'"></i>
                <span class="menu-text">{{ item.label }}</span>
              </a>
              <a *ngIf="item.external"
                 [href]="item.route"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="menu-link"
                 [attr.data-testid]="'nav-' + item.label.toLowerCase().replace(/ /g, '-')"
                 (click)="onMenuItemClick()">
                <i [class]="item.icon + ' menu-icon'"></i>
                <span class="menu-text">{{ item.label }} ↗</span>
              </a>
            </li>
          </ng-container>

          <!-- Profile + Settings are reachable from the topbar user menu -->
          <li class="menu-separator">
            <span class="separator-label">Account</span>
          </li>
          <li>
            <a class="menu-link logout-link" data-testid="sidebar-logout" (click)="onLogout()">
              <i class="pi pi-sign-out menu-icon"></i>
              <span class="menu-text">Logout</span>
            </a>
          </li>
        </ul>

        <!-- SCRUM-1395 — admin "last updated" freshness signal. Polls
             /api/admin/heartbeat on mount + every 60s (admin-only endpoint,
             so only rendered for admins to avoid 403 polling for others). -->
        <div *ngIf="isAdmin"
             class="sidebar-last-updated"
             data-testid="admin-sidebar-last-updated">
          {{ lastUpdatedLabel }}
        </div>
      </div>
    </aside>

    <!-- Backdrop overlay for mobile and expanded tablet -->
    <div class="layout-mask"
         *ngIf="(layout.sidebarOpen$ | async) && layout.isMobile"
         (click)="layout.closeSidebar()"></div>
    <div class="layout-mask layout-mask-tablet"
         *ngIf="layout.tabletRailExpanded$ | async"
         (click)="layout.closeSidebar()"></div>
  `,
    styles: [`
    /* ────────────────────────────────────────────────────────────────
       Sakai-aligned sidebar — PrimeNG 17 / Sakai dark theme.
       Zero tooltips, zero touch handlers. Pure CSS + routerLink.
       ──────────────────────────────────────────────────────────── */

    :host { display: block; }

    /* ── Sidebar shell ────────────────────────────────────────────── */
    .layout-sidebar {
      position: fixed;
      top: 70px;
      left: 0;
      bottom: 0;
      width: 250px;
      background: var(--surface-0);
      border-right: 1px solid var(--surface-border);
      overflow: hidden;
      z-index: 999;
      display: flex;
      flex-direction: column;
      transition: transform 0.2s ease, width 0.2s ease;
    }

    .sidebar-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0.5rem 0;
    }

    /* ── Menu list ────────────────────────────────────────────────── */
    .layout-menu {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    /* ── Section separator / header ───────────────────────────────── */
    .menu-separator {
      padding: 1rem 1rem 0.25rem 1rem;
    }

    .separator-label {
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--text-color-secondary);
      opacity: 0.7;
      user-select: none;
    }

    /* ── Menu item link — the clickable row ───────────────────────── */
    .menu-link {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.65rem 1rem;
      margin: 1px 0.5rem;
      border-radius: var(--border-radius);
      color: var(--text-color);
      text-decoration: none;
      font-size: 0.875rem;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
      /* Prevent iOS tap-highlight and double-tap-zoom */
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    .menu-link:hover {
      background: var(--surface-hover);
      color: var(--primary-color);
    }

    .menu-link:active {
      background: var(--surface-hover);
    }

    .menu-link.active-route {
      background: var(--primary-color);
      color: var(--primary-color-text, #ffffff);
      font-weight: 600;
    }

    .menu-link.active-route .menu-icon {
      color: var(--primary-color-text, #ffffff);
    }

    .menu-icon {
      font-size: 1rem;
      width: 1.25rem;
      text-align: center;
      flex-shrink: 0;
    }

    .menu-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* ── Logout link — red tint ───────────────────────────────────── */
    .logout-link {
      color: var(--red-400);
    }
    .logout-link:hover {
      background: rgba(239, 68, 68, 0.08);
      color: var(--red-500);
    }
    .logout-link .menu-icon {
      color: inherit;
    }

    /* ── SCRUM-1395 last-updated freshness signal ─────────────────── */
    .sidebar-last-updated {
      padding: 0.5rem 1rem 0.75rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.7rem;
      color: var(--text-color-secondary);
      opacity: 0.7;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
    }

    /* ── Backdrop masks ───────────────────────────────────────────── */
    .layout-mask {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      z-index: 998;
      animation: maskFade 0.2s ease;
    }
    .layout-mask-tablet {
      top: 70px;
    }
    @keyframes maskFade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
       TABLET / iPad (769px – 1199px): icon-only rail, expandable.
       ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
    @media screen and (min-width: 769px) and (max-width: 1199px) {
      .layout-sidebar {
        width: 64px;
      }

      /* Hide text, show only icons */
      .menu-text,
      .separator-label,
      .sidebar-last-updated {
        display: none;
      }

      /* SCRUM-2285 — a Tesla in-car screen (~1088px) sits in this tablet band
         but is NOT a tablet: keep the full labeled desktop sidebar so drivers
         get text labels, not a 64px icon-only rail. Non-Tesla tablets/iPads at
         the same width keep the rail above. */
      :host-context(html.tesla) .layout-sidebar { width: 250px; }
      :host-context(html.tesla) .menu-text,
      :host-context(html.tesla) .separator-label,
      :host-context(html.tesla) .sidebar-last-updated { display: revert; }
      :host-context(html.tesla) .menu-link {
        justify-content: flex-start;
        padding: 0.65rem 1rem;
        margin: 1px 0.5rem;
        gap: 0.75rem;
      }

      .menu-separator {
        padding: 0.5rem 0 0.15rem;
        border-top: 1px solid var(--surface-border);
        margin: 0.25rem 0.5rem 0;
      }
      .menu-separator:first-child {
        border-top: none;
        margin-top: 0;
      }

      .menu-link {
        justify-content: center;
        padding: 0.6rem 0;
        margin: 2px 0.4rem;
        gap: 0;
        min-height: 44px;
      }

      .menu-icon {
        font-size: 1.15rem;
      }

      /* Scrollbar hidden on narrow rail */
      .sidebar-scroll {
        scrollbar-width: none;
      }
      .sidebar-scroll::-webkit-scrollbar {
        display: none;
      }

      /* ── Expanded state (hamburger tap) ──────────────────────────── */
      .layout-sidebar.tablet-expanded {
        width: 250px;
        box-shadow: 4px 0 18px rgba(0, 0, 0, 0.15);
      }

      .layout-sidebar.tablet-expanded .menu-text,
      .layout-sidebar.tablet-expanded .separator-label {
        display: inline;
      }

      .layout-sidebar.tablet-expanded .sidebar-last-updated {
        display: block;
      }

      .layout-sidebar.tablet-expanded .menu-separator {
        padding: 1rem 1rem 0.25rem;
        border-top: none;
        margin: 0;
      }

      .layout-sidebar.tablet-expanded .menu-link {
        justify-content: flex-start;
        padding: 0.65rem 1rem;
        margin: 1px 0.5rem;
        gap: 0.75rem;
      }
    }

    /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
       MOBILE (≤768px): hidden drawer, slides in from left.
       ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
    @media screen and (max-width: 768px) {
      .layout-sidebar {
        transform: translateX(-100%);
        width: 260px;
        box-shadow: none;
        transition: transform 0.25s ease;
        /* Stop the drawer above the bottom-nav (56px + safe-area) so no menu
           item lands under bottom-nav.component.ts's fixed bottom bar (z:1100).
           Symptom before fix: taps on sidebar items whose Y-coordinate falls
           in the last 56px of the drawer route through to the bottom-nav
           instead — verified via elementFromPoint returning nav-tab class. */
        bottom: calc(56px + env(safe-area-inset-bottom, 0px));
      }

      .layout-sidebar.layout-sidebar-active {
        transform: translateX(0);
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
      }

      /* Apple HIG-compliant tap target on mobile (the tablet media query
         already sets this at line ~269; mobile inherited the base 36px). */
      .menu-link {
        min-height: 44px;
      }
    }
  `]
})
export class SidebarComponent implements OnInit, OnDestroy {
  layout = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);

  visibleSections: NavSection[] = [];

  // SCRUM-1395 — admin sidebar "last updated" heartbeat.
  isAdmin = false;
  lastUpdatedLabel = 'Updated —';
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly HEARTBEAT_INTERVAL_MS = 60_000;

  private readonly allSections: NavSection[] = [
    {
      label: 'Morning Brief',
      items: [
        { label: 'Morning Brief', icon: 'pi pi-home', route: '/dashboard' },
        { label: 'News', icon: 'pi pi-megaphone', route: '/news' },
        { label: 'Calendar', icon: 'pi pi-calendar', route: '/calendar' },
        { label: 'Funds', icon: 'pi pi-building', route: '/funds' }, // SCRUM-1862
        { label: 'SEC Filings', icon: 'pi pi-file', route: '/sec/daily' },
      ],
    },
    // 2026-06-09 — Options Analysis relocated from Trade to Discover per
    // Honman ("the options analysis under trade should be moved to Discover").
    // The Trade section still owns Order Builder / Templates / Backtest
    // pending a separate decision on whether to retire those.
    {
      label: 'Discover',
      items: [
        { label: 'Growth Scanner', icon: 'pi pi-search', route: '/scanner', roles: ['admin'] },
        { label: 'Flow Scanner', icon: 'pi pi-bolt', route: '/flow-scanner', roles: ['admin'] },
        { label: 'Analysis', icon: 'pi pi-chart-line', route: '/options-analysis' },
        // SCRUM-2165 — personal per-user verdict factor weights.
        { label: 'My Analysis Weights', icon: 'pi pi-sliders-h', route: '/my-analysis-weights' },
        { label: 'Pre-Market', icon: 'pi pi-clock', route: '/pre-market', roles: ['admin'] },
        { label: 'Sector Heatmap', icon: 'pi pi-th-large', route: '/sector-heatmap' },
        // 2026-07-24 (Honman) — Sentiment moved from the Research section into
        // Discover; the Research section is now empty and removed. Stock Research
        // (trade-signal engine, SCRUM-1628) is folded into Analysis / Company
        // Research, so Discover holds all research-style tools.
        { label: 'Sentiment', icon: 'pi pi-comment', route: '/sentiment', roles: ['admin'] },
      ],
    },
    // SCRUM-906 (2026-06-09) — Trade section removed entirely.
    //   Options Analysis was moved to Discover (bfadb05).
    //   Order Builder / Templates / Backtest were FAST_DECISION_SPEC
    //   leftovers with 0 user rows in dev + UAT.
    //   Routes + modules + backend endpoints + DB tables all dropped.
    // SCRUM-882 (2026-06-09) — Manage section collapsed into Portfolio.
    //   - Income Dashboard, Risk Dashboard, Alerts moved here per Honman.
    //   - Positions merged into /portfolios/all as a tab (Q1=B); the
    //     /positions route now redirects to /portfolios/all?tab=positions.
    //   - Trade Tracker removed entirely (Q2=A) — route + module + backend
    //     endpoints + DB table; 0 rows in dev + UAT, confirmed before delete.
    {
      label: 'Portfolio',
      roles: ['admin', 'investor'],
      items: [
        { label: 'Portfolio', icon: 'pi pi-briefcase', route: '/portfolios/all' },
        { label: 'Watchlist', icon: 'pi pi-star', route: '/watchlist' },
        { label: 'Dividend Income', icon: 'pi pi-dollar', route: '/dividend-income' },
        { label: 'Income Dashboard', icon: 'pi pi-dollar', route: '/income-dashboard', roles: ['admin'] },
        { label: 'Risk Dashboard', icon: 'pi pi-shield', route: '/risk-dashboard', roles: ['admin'] },
        { label: 'Accuracy', icon: 'pi pi-check-circle', route: '/overview-accuracy', roles: ['admin'] },
        { label: 'Alerts', icon: 'pi pi-bell', route: '/alerts', roles: ['admin'] },
        { label: 'Trade Journal', icon: 'pi pi-book', route: '/journal', roles: ['admin'] },
        { label: 'Tax Tracker', icon: 'pi pi-percentage', route: '/tax', roles: ['admin'] },
        { label: 'Strategies', icon: 'pi pi-cog', route: '/strategies', roles: ['admin'] },
      ],
    },
    {
      label: 'Administration',
      roles: ['admin'],
      items: [
        // Deployments, Logs, QA Dashboard, Epics moved to hub.alphagroves.com
        // (ops plane) — removed here to avoid duplicated surfaces.
        { label: 'System Status', icon: 'pi pi-server', route: '/admin/status' },
        { label: 'User Management', icon: 'pi pi-users', route: '/admin/users' },
        { label: 'Database', icon: 'pi pi-database', route: '/admin/database' },
        { label: 'Cron Jobs', icon: 'pi pi-clock', route: '/admin/crons' },
        { label: 'Brief Jobs', icon: 'pi pi-bolt', route: '/admin/jobs/sentiment-briefs' },
        // SCRUM-1565 — Providers Console consolidates API Keys + AI Models +
        // the AI cost dashboard (Usage/Requests/Pricing) into one page. The
        // legacy /admin/api-keys, /admin/ai/models, /admin/ai routes redirect
        // into it (admin.routes.ts), so a single nav entry now covers all
        // three (matches sidebar.component.ts).
        { label: 'Providers', icon: 'pi pi-cloud', route: '/admin/providers' },
        { label: 'All Stocks', icon: 'pi pi-database', route: '/admin/stocks' },
        { label: 'Dividend Universe', icon: 'pi pi-dollar', route: '/admin/dividend-universe' },
        { label: 'SEC Intelligence', icon: 'pi pi-shield', route: '/admin/sec-intelligence' },
        // SCRUM-1985 — versioned Morning Brief bias-engine dials editor.
        { label: 'Bias Rules', icon: 'pi pi-sliders-h', route: '/admin/bias-rules' },
        // SCRUM-2154 — 6-factor Analysis verdict master weights editor.
        { label: 'Analysis Weights', icon: 'pi pi-chart-bar', route: '/admin/analysis-weights' },
        // SCRUM-2163 — 6-factor verdict accuracy dashboard.
        { label: 'Analysis Accuracy', icon: 'pi pi-chart-line', route: '/admin/analysis-accuracy' },
        { label: 'Health', icon: 'pi pi-heart', route: '/admin/health' },
        // External — opens the platform Grafana (Keycloak-SSO'd).
        // Lives at hub.alphagroves.com/grafana/ behind ops-nginx; URL
        // is environment-stable because Cloudflare points the apex
        // there for all envs.
        { label: 'Grafana', icon: 'pi pi-chart-line', route: 'https://hub.alphagroves.com/grafana/', external: true },
      ],
    },
  ];

  ngOnInit(): void {
    this.updateVisibleSections();
    this.isAdmin = (this.authService.getCurrentRole?.() || 'investor') === 'admin';
    // Only poll the admin-only heartbeat endpoint for admins; other roles
    // would just get 403s every minute.
    if (this.isAdmin) {
      this.fetchHeartbeat();
      this.heartbeatTimer = setInterval(
        () => this.fetchHeartbeat(),
        SidebarComponent.HEARTBEAT_INTERVAL_MS,
      );
    }
  }

  ngOnDestroy(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // SCRUM-1395 — GET /api/admin/heartbeat. AuthInterceptor attaches the JWT.
  // On failure we keep the last known value (blanking the element would flake
  // Playwright); the initial label stays "Updated —" until the first success.
  private fetchHeartbeat(): void {
    this.http.get<{ updatedAt: string }>('/api/admin/heartbeat').subscribe({
      next: (res) => {
        const parsed = new Date(res?.updatedAt);
        if (!Number.isNaN(parsed.getTime())) {
          this.lastUpdatedLabel = `Updated ${this.formatRelative(parsed)}`;
        }
      },
      error: () => {
        // Keep the last known value; do not blank the element.
      },
    });
  }

  // Minimal inline relative-time formatter — the frontend ships no dayjs /
  // date-fns, and this story is English-only with no i18n keys.
  private formatRelative(from: Date): string {
    const seconds = Math.max(0, Math.floor((Date.now() - from.getTime()) / 1000));
    if (seconds < 45) return 'a few seconds ago';
    if (seconds < 90) return 'a minute ago';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  onMenuItemClick(): void {
    // Close the sidebar if it's overlaying content (mobile or expanded tablet)
    this.layout.closeIfOverlay();
  }

  // SCRUM-1725 — iOS Safari drops the synthesized `click` on the sidebar links
  // after the drawer is closed and re-opened (a `transform` on a position:fixed
  // element), so `routerLink` never fires and nav gets stuck. Navigate on
  // `pointerup` — the event Safari DOES deliver — for a genuine tap.
  // SCRUM-1953 — this dropped-click is a TOUCH problem, not a width one: iPad is
  // >768px so `isMobile` is false, yet Safari still drops the click there and
  // the old `if (!isMobile) return` gate skipped the fallback → sidebar nav was
  // dead on iPad. Gate on the input being touch instead: covers phone + iPad +
  // touch laptops, while mouse/trackpad keeps using the native routerLink.
  // Same-URL navigation is a no-op, so a redundant native click (when it isn't
  // dropped) causes no double-nav.
  private tapX = 0;
  private tapY = 0;

  onLinkPointerDown(ev: PointerEvent): void {
    this.tapX = ev.clientX;
    this.tapY = ev.clientY;
  }

  onLinkPointerUp(route: string, ev: PointerEvent): void {
    if (ev.pointerType !== 'touch') return; // mouse/trackpad: the native click works
    const moved = Math.abs(ev.clientX - this.tapX) + Math.abs(ev.clientY - this.tapY);
    if (moved > 10) return; // a scroll/swipe, not a tap
    this.onMenuItemClick();
    this.router.navigateByUrl(route);
  }

  onLogout(): void {
    this.authService.logout();
  }

  // SCRUM-537 — swipe-left-to-close gesture. Active only on mobile widths
  // (matches the existing "close on backdrop tap" pattern). Thresholds:
  //   • horizontal travel > 40px
  //   • horizontal:vertical ratio > 1.4 — keeps vertical scrolls from
  //     accidentally closing the sidebar.
  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private readonly SWIPE_MIN_X = 40;
  private readonly SWIPE_RATIO = 1.4;

  onTouchStart(ev: TouchEvent): void {
    if (!this.layout.isMobile) return;
    const t = ev.touches[0];
    if (!t) return;
    this.touchStartX = t.clientX;
    this.touchStartY = t.clientY;
  }

  onTouchMove(_ev: TouchEvent): void {
    // No-op for now — kept as a hook for future "drawer follows finger"
    // animation. Bound so the (touchmove) binding doesn't error in
    // strict-template mode.
  }

  onTouchEnd(ev: TouchEvent): void {
    if (this.touchStartX == null || this.touchStartY == null) return;
    const t = ev.changedTouches[0];
    if (!t) { this.touchStartX = null; this.touchStartY = null; return; }
    const dx = t.clientX - this.touchStartX;
    const dy = Math.abs(t.clientY - this.touchStartY);
    this.touchStartX = null;
    this.touchStartY = null;
    // Negative dx = swipe LEFT. The sidebar is on the left edge, so closing
    // it means moving the user's finger toward the left — that's correct
    // muscle memory for "push the drawer off-screen".
    if (-dx >= this.SWIPE_MIN_X && Math.abs(dx) >= dy * this.SWIPE_RATIO) {
      this.layout.closeSidebar();
    }
  }

  private updateVisibleSections(): void {
    const role = this.authService.getCurrentRole?.() || 'investor';
    // SCRUM-2342 — filter sections by role, then also filter each section's
    // items by role so an item can be admin-only even inside a shared section.
    // A section whose items all get filtered out is dropped.
    this.visibleSections = this.allSections
      .filter(s => !s.roles || s.roles.includes(role))
      .map(s => ({ ...s, items: s.items.filter(i => !i.roles || i.roles.includes(role)) }))
      .filter(s => s.items.length > 0);
  }
}
